import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Car, 
  MapPin, 
  Shield, 
  Zap, 
  Clock, 
  Smartphone,
  ChevronRight,
  Command,
  Users,
  DollarSign,
  Star,
  X,
  Loader2,
  Lock
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const Landing = () => {
  const navigate = useNavigate();
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    try {
      // Try logging in as a driver first
      const response = await api.post('/auth/login', {
        email: adminEmail,
        password: adminPassword,
        user_type: 'driver'
      });
      
      // Store token with correct key
      localStorage.setItem('reddi_token', response.data.token);
      
      // Check if user is admin
      const meResponse = await api.get('/auth/me');
      
      if (meResponse.data.is_admin) {
        localStorage.setItem('reddi_user', JSON.stringify(meResponse.data));
        toast.success('Welcome back, Admin!');
        // Force full page reload to update auth context
        window.location.href = '/admin';
      } else {
        localStorage.removeItem('reddi_token');
        toast.error('Access denied. Admin privileges required.');
      }
    } catch (error) {
      // Try rider login
      try {
        const response = await api.post('/auth/login', {
          email: adminEmail,
          password: adminPassword,
          user_type: 'rider'
        });
        
        localStorage.setItem('reddi_token', response.data.token);
        
        const meResponse = await api.get('/auth/me');
        
        if (meResponse.data.is_admin) {
          localStorage.setItem('reddi_user', JSON.stringify(meResponse.data));
          toast.success('Welcome back, Admin!');
          // Force full page reload to update auth context
          window.location.href = '/admin';
        } else {
          localStorage.removeItem('reddi_token');
          toast.error('Access denied. Admin privileges required.');
        }
      } catch (err) {
        localStorage.removeItem('reddi_token');
        toast.error('Invalid credentials or not an admin account');
      }
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] overflow-x-hidden">
      {/* Hero Section with Background Image */}
      <section className="relative h-screen flex items-end pb-20">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1920&q=80')`,
          }}
        />
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/40 via-[#020617]/70 to-[#020617]"></div>
        
        {/* Red Glow Effect */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(220,38,38,0.15)_0%,transparent_50%)]"></div>

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-30 px-6 md:px-12 lg:px-24 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-12 h-12 bg-red-600 flex items-center justify-center transform -skew-x-6 group-hover:skew-x-0 transition-transform duration-300">
                <Car className="w-7 h-7 text-white" />
              </div>
              <span className="text-3xl font-['Barlow_Condensed'] font-black text-white tracking-tight">REDDI</span>
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/login" className="text-slate-300 hover:text-white font-medium transition-colors">
                Log in
              </Link>
              <Link to="/register">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-['Barlow_Condensed'] font-bold uppercase tracking-widest px-6 py-3 rounded-none transform -skew-x-6 hover:skew-x-0 transition-all duration-300" data-testid="signup-btn">
                  Sign up
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-20 px-6 md:px-12 lg:px-24 w-full">
          <div className="max-w-4xl">
            <h1 className="text-6xl sm:text-7xl lg:text-8xl xl:text-9xl font-['Barlow_Condensed'] font-black text-white uppercase leading-[0.85] tracking-tighter">
              Move<br />
              <span className="text-red-600">Faster</span>
            </h1>
            <p className="mt-8 text-xl text-slate-400 max-w-lg font-light">
              The city never sleeps. Neither do we. Get where you need to be in minutes, not hours.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link to="/register?type=rider" data-testid="get-started-rider-btn">
                <Button className="group bg-red-600 hover:bg-red-700 text-white font-['Barlow_Condensed'] font-bold uppercase tracking-widest px-10 py-6 text-lg rounded-none transform -skew-x-6 hover:skew-x-0 transition-all duration-300 shadow-[0_0_30px_rgba(220,38,38,0.3)] hover:shadow-[0_0_50px_rgba(220,38,38,0.5)]">
                  <span className="inline-block transform skew-x-6 group-hover:skew-x-0 transition-transform">Ride Now</span>
                  <ChevronRight className="w-5 h-5 ml-2 inline-block transform skew-x-6 group-hover:skew-x-0 group-hover:translate-x-1 transition-all" />
                </Button>
              </Link>
              <Link to="/register?type=driver" data-testid="get-started-driver-btn">
                <Button variant="outline" className="bg-transparent border border-slate-700 text-slate-300 hover:text-white hover:border-white font-['Barlow_Condensed'] font-semibold uppercase tracking-wider px-10 py-6 text-lg rounded-none transition-all duration-300">
                  Become a Driver
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-red-600 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Value Props Bar */}
      <section className="relative z-10 bg-slate-900/80 backdrop-blur-xl border-y border-white/5 py-8 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-4xl font-['Barlow_Condensed'] font-black text-white">85%</div>
            <div className="text-slate-500 text-sm uppercase tracking-wider mt-1">Driver Earnings</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-['Barlow_Condensed'] font-black text-white">$2.99</div>
            <div className="text-slate-500 text-sm uppercase tracking-wider mt-1">Flat Service Fee</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-['Barlow_Condensed'] font-black text-red-600">24/7</div>
            <div className="text-slate-500 text-sm uppercase tracking-wider mt-1">Always Available</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-['Barlow_Condensed'] font-black text-white">100%</div>
            <div className="text-slate-500 text-sm uppercase tracking-wider mt-1 flex items-center justify-center gap-1">
              <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> Tips to Drivers
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative">
        {/* Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-30"></div>
        
        <div className="relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-['Barlow_Condensed'] font-black text-white uppercase tracking-tight">
              Built for <span className="text-red-600">Speed</span>
            </h2>
            <p className="mt-4 text-slate-500 max-w-xl mx-auto">
              Every feature engineered for maximum efficiency
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Large Card - Instant Hail */}
            <div className="md:col-span-2 md:row-span-2 bg-slate-900/40 backdrop-blur-md border border-white/5 hover:border-red-600/50 transition-all duration-500 p-8 group">
              <div className="h-full flex flex-col">
                <div className="w-16 h-16 bg-red-600/20 flex items-center justify-center mb-6 transform -skew-x-6 group-hover:skew-x-0 transition-transform">
                  <Zap className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-2xl font-['Barlow_Condensed'] font-bold text-white uppercase mb-3">Instant Hail</h3>
                <p className="text-slate-400 mb-6 flex-grow">
                  Average pickup time under 3 minutes. Our smart matching algorithm connects you with the nearest driver instantly.
                </p>
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-slate-700 border-2 border-slate-900"></div>
                    <div className="w-8 h-8 rounded-full bg-slate-600 border-2 border-slate-900"></div>
                    <div className="w-8 h-8 rounded-full bg-slate-500 border-2 border-slate-900"></div>
                  </div>
                  <span className="text-slate-500 text-sm">Drivers ready in your area</span>
                </div>
              </div>
            </div>

            {/* Safety Card */}
            <div className="bg-slate-800 border border-slate-700 p-6 hover:border-red-600/30 transition-colors">
              <Shield className="w-10 h-10 text-emerald-500 mb-4" />
              <h3 className="text-lg font-['Barlow_Condensed'] font-bold text-white uppercase mb-2">Safety First</h3>
              <p className="text-slate-400 text-sm">
                All drivers background checked. Real-time ride sharing. Emergency SOS button.
              </p>
            </div>

            {/* Smart Routing Card */}
            <div className="md:row-span-2 bg-slate-900/40 backdrop-blur-md border border-white/5 hover:border-red-600/50 transition-all duration-500 p-6 flex flex-col">
              <MapPin className="w-10 h-10 text-red-500 mb-4" />
              <h3 className="text-lg font-['Barlow_Condensed'] font-bold text-white uppercase mb-2">Smart Routing</h3>
              <p className="text-slate-400 text-sm flex-grow">
                AI-powered navigation finds the fastest route. Live traffic updates. ETAs you can trust.
              </p>
              <div className="mt-4 p-3 bg-slate-950/50 border border-slate-800">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Current ETA</span>
                  <span className="text-emerald-400 font-mono">2:47</span>
                </div>
              </div>
            </div>

            {/* 24/7 Support */}
            <div className="bg-slate-800 border border-slate-700 p-6 hover:border-red-600/30 transition-colors">
              <Clock className="w-10 h-10 text-amber-500 mb-4" />
              <h3 className="text-lg font-['Barlow_Condensed'] font-bold text-white uppercase mb-2">24/7 Support</h3>
              <p className="text-slate-400 text-sm">
                Round-the-clock customer support. We're always here when you need us.
              </p>
            </div>

            {/* In-App Chat */}
            <div className="bg-slate-800 border border-slate-700 p-6 hover:border-red-600/30 transition-colors">
              <Smartphone className="w-10 h-10 text-cyan-500 mb-4" />
              <h3 className="text-lg font-['Barlow_Condensed'] font-bold text-white uppercase mb-2">In-App Chat</h3>
              <p className="text-slate-400 text-sm">
                Message your driver directly. No need to share your phone number.
              </p>
            </div>

            {/* Multi-Language */}
            <div className="bg-slate-800 border border-slate-700 p-6 hover:border-red-600/30 transition-colors">
              <Users className="w-10 h-10 text-purple-500 mb-4" />
              <h3 className="text-lg font-['Barlow_Condensed'] font-bold text-white uppercase mb-2">3 Languages</h3>
              <p className="text-slate-400 text-sm">
                English, Spanish, French. More coming soon. Ride in your language.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-['Barlow_Condensed'] font-black text-white uppercase tracking-tight leading-tight">
                Experience<br />
                <span className="text-red-600">Seamless Rides</span>
              </h2>
              <p className="mt-6 text-slate-400 text-lg">
                See how Reddi makes getting around your city easier, faster, and more affordable.
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-red-600/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-slate-300">Real-time driver tracking</span>
                </li>
                <li className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-red-600/20 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-slate-300">Turn-by-turn navigation</span>
                </li>
                <li className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-red-600/20 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-slate-300">Seamless payment experience</span>
                </li>
              </ul>
            </div>
            <div className="relative aspect-video rounded-sm overflow-hidden border border-slate-800">
              <img
                src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80"
                alt="Ride experience"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Driver Recruitment Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=1200&q=80')`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/90 to-[#020617]/70"></div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 mb-6">
                <span className="text-emerald-400 font-mono text-sm uppercase tracking-wider">Now Hiring</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-['Barlow_Condensed'] font-black text-white uppercase tracking-tight">
                Drive With<br />
                <span className="text-red-600">Reddi</span>
              </h2>
              <p className="mt-6 text-slate-400 text-lg">
                Turn your car into a money-making machine. Set your own hours. Keep 85% of every fare plus 100% of tips.
              </p>
              
              <div className="mt-10 grid grid-cols-2 gap-6">
                <div className="p-4 bg-slate-900/60 backdrop-blur border border-white/5">
                  <DollarSign className="w-8 h-8 text-emerald-500 mb-2" />
                  <div className="text-2xl font-['Barlow_Condensed'] font-bold text-white">$25/hr</div>
                  <div className="text-slate-500 text-sm">Avg. Earnings</div>
                </div>
                <div className="p-4 bg-slate-900/60 backdrop-blur border border-white/5">
                  <Clock className="w-8 h-8 text-amber-500 mb-2" />
                  <div className="text-2xl font-['Barlow_Condensed'] font-bold text-white">Flexible</div>
                  <div className="text-slate-500 text-sm">Your Schedule</div>
                </div>
              </div>

              <Link to="/register?type=driver" className="inline-block mt-10">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 font-['Barlow_Condensed'] font-bold uppercase tracking-widest px-10 py-5 text-lg rounded-none transform -skew-x-6 hover:skew-x-0 transition-all duration-300" data-testid="start-driving-btn">
                  Start Driving Today
                </Button>
              </Link>
            </div>

            <div className="relative hidden lg:block">
              <img 
                src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80"
                alt="Happy Reddi driver"
                className="w-full rounded-sm border border-slate-800"
              />
              <div className="absolute -bottom-6 -left-6 p-4 bg-slate-900 border border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <span className="text-white font-mono text-sm">4.9 Driver Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 bg-[#020617] border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-red-600 flex items-center justify-center transform -skew-x-6">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-['Barlow_Condensed'] font-black text-white">REDDI</span>
              </Link>
              <p className="text-slate-500 text-sm">
                The pulse of the city. Moving you forward, faster.
              </p>
            </div>
            <div>
              <h4 className="font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">Ride</h4>
              <ul className="space-y-2">
                <li><Link to="/register?type=rider" className="text-slate-500 hover:text-white text-sm transition-colors">Book a Ride</Link></li>
                <li><Link to="/login" className="text-slate-500 hover:text-white text-sm transition-colors">Sign In</Link></li>
                <li><Link to="/safety" className="text-slate-500 hover:text-white text-sm transition-colors">Safety</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">Drive</h4>
              <ul className="space-y-2">
                <li><Link to="/register?type=driver" className="text-slate-500 hover:text-white text-sm transition-colors">Become a Driver</Link></li>
                <li><Link to="/driver-requirements" className="text-slate-500 hover:text-white text-sm transition-colors">Driver Requirements</Link></li>
                <li><Link to="/earnings" className="text-slate-500 hover:text-white text-sm transition-colors">Earnings</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-['Barlow_Condensed'] font-bold text-white uppercase mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-500 hover:text-white text-sm transition-colors">About</Link></li>
                <li><Link to="/careers" className="text-slate-500 hover:text-white text-sm transition-colors">Careers</Link></li>
                <li><Link to="/contact" className="text-slate-500 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-slate-600 text-sm font-mono">
              © 2025 REDDI. ALL RIGHTS RESERVED.
            </div>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-slate-500 hover:text-white text-sm transition-colors">Terms</Link>
              <Link to="/privacy" className="text-slate-500 hover:text-white text-sm transition-colors">Privacy</Link>
              <Link to="/help" className="text-slate-500 hover:text-white text-sm transition-colors">Help</Link>
              <button 
                onClick={() => setAdminModalOpen(true)} 
                className="text-slate-600 hover:text-slate-400 text-sm transition-colors flex items-center gap-1"
                data-testid="admin-login-footer-btn"
              >
                <Command className="w-3 h-3" />
                <span>Admin</span>
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Login Modal */}
      {adminModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setAdminModalOpen(false)}></div>
          <div className="relative w-full max-w-md bg-[#0a0a0a] border border-slate-700 p-8" data-testid="admin-login-modal">
            <div className="relative z-10">
              <button 
                onClick={() => setAdminModalOpen(false)}
                className="absolute top-0 right-0 text-slate-500 hover:text-white"
                data-testid="close-admin-modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-800 flex items-center justify-center border border-slate-700">
                  <Command className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <h3 className="font-['Barlow_Condensed'] font-bold text-white uppercase text-xl">Admin Login</h3>
                  <p className="text-slate-500 text-xs">Authorized access only</p>
                </div>
              </div>

              <form onSubmit={handleAdminLogin} className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-xs uppercase mb-2">Email</label>
                  <Input
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    className="bg-slate-950/50 border-slate-800 focus:border-red-500 focus:ring-1 focus:ring-red-500/50 rounded-none h-12 text-sm text-white"
                    placeholder="admin@reddi.com"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs uppercase mb-2">Password</label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="bg-slate-950/50 border-slate-800 focus:border-red-500 focus:ring-1 focus:ring-red-500/50 rounded-none h-12 text-sm text-white"
                    placeholder="••••••••"
                    required
                    data-testid="admin-password-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={adminLoading}
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-['Barlow_Condensed'] uppercase tracking-widest py-4 rounded-none"
                  data-testid="admin-login-submit"
                >
                  {adminLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-800 text-center">
                <Link to="/admin" className="text-slate-500 hover:text-red-400 text-xs transition-colors">
                  New admin? Register with secret key →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
